import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { ImageType } from '@models/Image';
import { buildImageSrc, getImageAttrFromHeader } from '@utils/imageUtils';

type UseImageHeadersAttr = {
  /**
   * If `true`, disables query caching
   */
  disableCache?: boolean;
  /**
   * If true, disables the image fetching
   */
  disabled?: boolean;
  /**
   * Image identifier used for query caching
   */
  identifier: string;
  /**
   * Type of image (used for caching and image fetching)
   */
  imageType: ImageType;
  /**
   * Source URL of the image to fetch
   */
  src?: string;
};

export type ImageHeaders = {
  getSrc: (width?: number) => string;
  isDeletable?: boolean;
  isFallback?: boolean;
  isLoading?: boolean;
  originalHeight?: number;
  originalWidth?: number;
  refetch?: VoidFunction;
  wasFound?: boolean;
};

/**
 * Fetches image headers to determine attributes such as original dimensions and if the image is a fallback
 * @param src Base URL of the image
 * @returns Image attributes extracted from the headers
 */
const fetchImageHeaders = async (src: string) => {
  const response = await fetch(new Request(src, { cache: 'no-store' }));
  // const response = await fetch(new Request(src));
  if (!response.ok) throw new Error('Network response was not ok');
  return getImageAttrFromHeader(response.headers);
};

/**
 * Custom hook to manage image fetching and attributes
 * @returns Image attributes and handlers
 */
const useImageHeaders = ({
  disabled,
  disableCache,
  identifier,
  imageType,
  src,
}: UseImageHeadersAttr): ImageHeaders => {
  /**
   * State to force image reloads by updating the hash appended to the image URL. Temporary solution
   * used until CDN fully supports cache invalidation in all environments.
   */
  const [hash, setHash] = useState(() => Date.now());
  const imageHash = disableCache ? hash : undefined;

  const {
    data: { isDeletable, isFallback, wasFound, originalHeight, originalWidth } = {},
    isLoading,
    refetch,
  } = useQuery({
    enabled: !disabled && Boolean(src),
    queryFn: () => fetchImageHeaders(buildImageSrc(src, imageHash, 0)),
    queryKey: ['image', imageType, identifier, ...(disableCache ? [hash] : [])],
    refetchOnMount: false,
    staleTime: disableCache ? 0 : Infinity,
  });

  /**
   * Function to force refetching the image by updating the hash
   */
  const refetchFn = useCallback(
    () => (disableCache ? setHash(Date.now()) : refetch()),
    [disableCache, refetch]
  );

  /**
   * Builds the full image source URL with optional width parameter
   * @param width Optional width to resize the image
   */
  const getSrc = useCallback(
    (width?: number) => buildImageSrc(src, imageHash, width),
    [imageHash, src]
  );

  return {
    getSrc,
    isDeletable,
    isFallback,
    isLoading,
    originalHeight,
    originalWidth,
    refetch: refetchFn,
    wasFound,
  };
};

export default useImageHeaders;
